import {
  config,
  TWIN_TYPE,
  isPregnancyNode,
  pregnancyLossRenderLabel,
  isStillbirth,
  isOngoingPregnancy,
  isReproNode,
  isReproSatelliteNode,
  donorRoleLabelOf
} from './config';
import * as d3 from 'd3';
import { siblingTypeForCount } from './twinLinkage';
import {
  labelMaxChars,
  blockLines,
  buildGenotypeRuns,
  estimateNoteLines,
  fertilityLabelClearance,
  LABEL_ROW_PITCH,
  STACKED_LABEL_BLOCKS
} from './labelText.js';
import { labelsForNodeAt } from 'src/components/pedigree-chart/labelScope';
import { effectiveLabelOrder } from './labelDefinitions';
import { sortLabelValues } from './labelValueSort';
import { filterLabelValues } from './labelValueFilter';

export const relationship = {
  mother: 'mother',
  father: 'father',
  son: 'son',
  daughter: 'daughter',
  fullSister: 'fullSister',
  fullBrother: 'fullBrother',
  halfSister: 'halfSister',
  halfBrother: 'halfBrother',
  spouse: 'spouse',
  mothersMother: 'mothersMother',
  mothersFather: 'mothersFather',
  fathersMother: 'fathersMother',
  fathersFather: 'fathersFather',
  mothersBrother: 'mothersBrother',
  mothersSister: 'mothersSister',
  fathersBrother: 'fathersBrother',
  fathersSister: 'fathersSister',
  otherAffected: 'otherAffected'
};

export const ADOPTED = {
  ADOPTED_IN: 'adopted_in',
  ADOPTED_OUT: 'adopted_out'
};

// Bennett 2022: an adopted-IN individual's ENTIRE line of descent is dashed (a uniform
// pattern over the whole path); every other edge stays solid. Pure so the render decision is
// unit-testable without mounting the D3 canvas. Returns undefined (no dasharray) otherwise.
export const ADOPTED_IN_DASHARRAY = '30 20';
export const adoptedDescentDash = (child) =>
  child?.adopted === ADOPTED.ADOPTED_IN ? ADOPTED_IN_DASHARRAY : undefined;

// Dual-parent adoption (Bennett Fig 3): resolve the SOLID biological-link connectors. For each
// node that references in-graph biological parents (bioFatherId / bioMotherId — pids), return
// { child, bioFather, bioMother } with the resolved parent nodes (either may be null if only
// one is linked / present). Pure: Canvas gates the call on ENABLE_ADOPTION_DUAL_PARENT and
// supplies laid-out nodes (with x/y) to draw from. Empty when nothing is linked, so the
// no-adoption render is unchanged.
export function biologicalConnectors(nodes) {
  if (!Array.isArray(nodes)) return [];
  const byPid = new Map();
  nodes.forEach((n) => {
    if (n && n.pid != null) byPid.set(n.pid, n);
  });
  const out = [];
  nodes.forEach((n) => {
    if (!n) return;
    const bioFather = n.bioFatherId != null ? byPid.get(n.bioFatherId) : null;
    const bioMother = n.bioMotherId != null ? byPid.get(n.bioMotherId) : null;
    if (bioFather || bioMother) {
      out.push({
        child: n,
        bioFather: bioFather || null,
        bioMother: bioMother || null
      });
    }
  });
  return out;
}

// Collision-aware slot finder for parking the adopted-in biological-parent couple on the
// adoptive parents' row (Bennett Fig 3 dual-parent adoption). Given the occupied x-positions on
// that row (ASCENDING) to the right of the adoptive parents, a desired start x, the couple's
// span (0 for a single parent, one gap for a pair) and the inter-node gap, return the first start
// x at which the couple's footprint [start-gap, start+span+gap] clears every occupied node —
// stepping one gap past any node in the way. Pure geometry so it is unit-testable without a
// laid-out family; buildRenderModel supplies the row scan.
// Is a Reproductive label switched on? Shared by the Canvas render filters and the label-row
// reservation in computeLabelLayout, which MUST agree — a reserved row whose label does not print
// leaves a blank gap, and a printed label with no reserved row overlaps its neighbour.
//
// The four annotations that predate the Reproductive label group (outcome, gestational age,
// fetal-sex method, karyotype) rendered UNCONDITIONALLY, so label presets saved before the group
// existed carry no key for them. Treat MISSING as ON — only an explicit `false` (a deliberate
// toggle-off) hides them — otherwise adding the group would silently blank annotations on every
// existing chart. `fetusName` is new output and is therefore opt-IN. Same `!== false` idiom the
// analysis-overlay/nomenclature toggles already use.
const REPRO_LABELS_OPT_IN = new Set(['fetusName', 'donorRole']);
export const reproLabelOn = (ls, key) =>
  REPRO_LABELS_OPT_IN.has(key) ? !!(ls && ls[key]) : !(ls && ls[key] === false);

export function firstClearSlot(occupiedXsAsc, startX, coupleSpan, gap) {
  let x = startX;
  for (const ox of occupiedXsAsc) {
    if (ox >= x - gap && ox <= x + coupleSpan + gap) {
      x = ox + gap; // this node is in the footprint → step one slot past it
    }
  }
  return x;
}

export const genderIconMap = (data) => {
  const gender = data.genderIdentity || data.gender;
  const map = {
    male: 'male',
    female: 'female',
    unknown: 'unknown',
    Male: 'male',
    Female: 'female',
    Indeterminate: 'unknown',
    'Male - AFAB': 'male',
    'Male - UAAB': 'male',
    'Female - AMAB': 'female',
    'Female - UAAB': 'female',
    'Non-binary/gender diverse - AFAB': 'unknown',
    'Non-binary/gender diverse - AMAB': 'unknown',
    'Non-binary/gender diverse - UAAB': 'unknown',
    'Unknown/Not Stated Unknown': 'unknown'
  };

  return map[gender];
};

export const validatePedigreeConfig = ({ user, members }) => {
  let dummyCounter = 0;
  const createDummyNode = (gender) => {
    const id = `dummy-${++dummyCounter}`;
    return {
      pid: id,
      uid: id,
      gender,
      isDummy: true,
      fatherId: null,
      motherId: null,
      spouseIds: [],
      childrenIds: [],
      siblingIds: []
    };
  };

  const familyMap = new Map();
  const allNodes = [user, ...members].map((m) => {
    const id = m.pid || m.uid || m._id;
    // Preserve the original uid if it exists; otherwise prefer the DB _id
    // string so the identifier remains stable across saves. Falling back to
    // the local pid here (as we used to) made every save look like a fresh
    // node to the backend, which inserted a new FamilyChanged row instead
    // of updating in place.
    const stableUid = m.uid || (m._id ? String(m._id) : id);
    return {
      ...m,
      pid: id,
      uid: stableUid,
      fatherId: m.fatherId || null,
      motherId: m.motherId || null,
      spouseIds: Array.isArray(m.spouseIds) ? [...m.spouseIds] : [],
      childrenIds: [],
      siblingIds: []
    };
  });

  allNodes.forEach((m) => familyMap.set(String(m.pid), m));
  const proband = familyMap.get(String(user.pid || user._id || user.uid));

  //parents
  allNodes.forEach((m) => {
    if (m.relationship === relationship.mother) proband.motherId = m.pid;
    if (m.relationship === relationship.father) proband.fatherId = m.pid;
  });

  const mom = allNodes.find((x) => x.relationship === relationship.mother);
  const dad = allNodes.find((x) => x.relationship === relationship.father);

  if (mom && dad) {
    if (!mom.spouseIds.includes(dad.pid)) mom.spouseIds.push(dad.pid);
    if (!dad.spouseIds.includes(mom.pid)) dad.spouseIds.push(mom.pid);
  }

  // grandparents
  allNodes.forEach((m) => {
    if (mom) {
      if (m.relationship === relationship.mothersMother) mom.motherId = m.pid;
      if (m.relationship === relationship.mothersFather) mom.fatherId = m.pid;
    }
    if (dad) {
      if (m.relationship === relationship.fathersMother) dad.motherId = m.pid;
      if (m.relationship === relationship.fathersFather) dad.fatherId = m.pid;
    }
  });

  // assign parents for siblings
  allNodes.forEach((m) => {
    if (
      m.relationship === relationship.fullBrother ||
      m.relationship === relationship.fullSister
    ) {
      m.fatherId = proband.fatherId;
      m.motherId = proband.motherId;
    }

    // father-siblings
    if (
      m.relationship === relationship.fathersBrother ||
      m.relationship === relationship.fathersSister
    ) {
      if (dad) {
        m.fatherId = dad.fatherId;
        m.motherId = dad.motherId;
      }
    }

    // mother-siblings
    if (
      m.relationship === relationship.mothersBrother ||
      m.relationship === relationship.mothersSister
    ) {
      if (mom) {
        m.fatherId = mom.fatherId;
        m.motherId = mom.motherId;
      }
    }

    // spouse
    if (m.relationship === relationship.spouse) {
      if (!proband.spouseIds.includes(m.pid)) proband.spouseIds.push(m.pid);
      if (!m.spouseIds.includes(proband.pid)) m.spouseIds.push(proband.pid);
    }
  });

  // children
  const probandGender = genderIconMap(proband); // 'male' | 'female' | 'unknown'
  let childDummySpouse = null;

  allNodes.forEach((m) => {
    if (
      m.relationship !== relationship.son &&
      m.relationship !== relationship.daughter
    )
      return;

    // 1. Honor the explicit biologicalParentId link when set.
    let otherParent = null;
    if (m.biologicalParentId) {
      const bioParentId = String(m.biologicalParentId);
      otherParent = allNodes.find(
        (n) =>
          n.pid !== proband.pid &&
          (String(n._id) === bioParentId || String(n.pid) === bioParentId)
      );
    }

    // 2. Legacy/no-explicit-link fallback: use the proband's primary spouse
    // (the non-auto-created one) so existing data keeps rendering correctly.
    if (!otherParent) {
      otherParent = allNodes.find(
        (x) =>
          x.relationship === relationship.spouse && !x.isAutoCreatedBioParent
      );
    }

    // 3. Still nothing → shared dummy partner for the proband.
    if (!otherParent) {
      if (!childDummySpouse) {
        const dummyGender = probandGender === 'female' ? 'male' : 'female';
        childDummySpouse = createDummyNode(dummyGender);
        familyMap.set(String(childDummySpouse.pid), childDummySpouse);
        if (!proband.spouseIds.includes(childDummySpouse.pid))
          proband.spouseIds.push(childDummySpouse.pid);
        childDummySpouse.spouseIds.push(proband.pid);
      }
      otherParent = childDummySpouse;
    }

    if (probandGender === 'female') {
      m.motherId = proband.pid;
      m.fatherId = otherParent.pid;
    } else {
      // male or unknown
      m.fatherId = proband.pid;
      m.motherId = otherParent.pid;
    }
  });

  //  half siblings
  const dummyBySharedParentId = new Map();
  allNodes.forEach((m) => {
    if (
      m.relationship !== relationship.halfBrother &&
      m.relationship !== relationship.halfSister
    )
      return;

    const halfParentId = m.halfParent ? String(m.halfParent) : null;

    let sharedParent = null;
    if (halfParentId) {
      if (
        mom &&
        (String(mom._id) === halfParentId || String(mom.pid) === halfParentId)
      )
        sharedParent = mom;
      else if (
        dad &&
        (String(dad._id) === halfParentId || String(dad.pid) === halfParentId)
      )
        sharedParent = dad;
    }

    if (!sharedParent) {
      const dummyFather = createDummyNode('male');
      const dummyMother = createDummyNode('female');
      familyMap.set(String(dummyFather.pid), dummyFather);
      familyMap.set(String(dummyMother.pid), dummyMother);
      dummyFather.spouseIds.push(dummyMother.pid);
      dummyMother.spouseIds.push(dummyFather.pid);
      m.fatherId = dummyFather.pid;
      m.motherId = dummyMother.pid;
      return;
    }

    const otherParentRef = m.otherParent ? String(m.otherParent) : null;
    let realOtherParent = null;
    if (otherParentRef && /^[a-f\d]{24}$/i.test(otherParentRef)) {
      realOtherParent =
        familyMap.get(otherParentRef) ||
        allNodes.find(
          (n) =>
            String(n._id || '') === otherParentRef ||
            String(n.pid || '') === otherParentRef
        );
    }

    if (realOtherParent) {
      if (!sharedParent.spouseIds.includes(realOtherParent.pid))
        sharedParent.spouseIds.push(realOtherParent.pid);
      if (!realOtherParent.spouseIds.includes(sharedParent.pid))
        realOtherParent.spouseIds.push(sharedParent.pid);

      if (sharedParent.relationship === relationship.mother) {
        m.motherId = sharedParent.pid;
        m.fatherId = realOtherParent.pid;
      } else {
        m.fatherId = sharedParent.pid;
        m.motherId = realOtherParent.pid;
      }
      return;
    }

    // Legacy / unresolved case — `otherParent` is either empty, a free-text
    // name from a pre-migration save, or an id we can't resolve in this batch.
    // Key the cache on BOTH the shared parent and the other-parent label so
    // two half-siblings who share the same real parent but have *different*
    // other parents (e.g. "Jane" vs "Adam") each get their own dummy node.
    const otherParentName = (
      typeof m.otherParent === 'string' ? m.otherParent : ''
    )
      .trim()
      .toLowerCase();
    const sharedParentKey = `${String(sharedParent.pid)}__${
      otherParentName || '__unknown'
    }`;

    let cacheEntry = dummyBySharedParentId.get(sharedParentKey);
    let dummyParent = cacheEntry ? cacheEntry.dummy : undefined;
    if (!dummyParent) {
      const dummyGender =
        sharedParent.relationship === relationship.mother ? 'male' : 'female';
      dummyParent = createDummyNode(dummyGender);

      if (typeof m.otherParent === 'string' && m.otherParent.trim()) {
        const nameParts = m.otherParent.trim().split(/\s+/);
        dummyParent.firstName = nameParts[0] || '';
        dummyParent.lastName = nameParts.slice(1).join(' ') || '';
      }
      familyMap.set(String(dummyParent.pid), dummyParent);
      dummyBySharedParentId.set(sharedParentKey, {
        dummy: dummyParent,
        sharedParentPid: String(sharedParent.pid)
      });

      // Link spouse
      if (!sharedParent.spouseIds.includes(dummyParent.pid))
        sharedParent.spouseIds.push(dummyParent.pid);
      dummyParent.spouseIds.push(sharedParent.pid);
    }

    // half siblings parents
    if (sharedParent.relationship === relationship.mother) {
      m.motherId = sharedParent.pid;
      m.fatherId = dummyParent.pid;
    } else {
      m.fatherId = sharedParent.pid;
      m.motherId = dummyParent.pid;
    }
  });

  // When members are saved via the pedigree editor, fatherId/motherId/spouseIds are stored
  // as normalized sequential integers (from normalizeIds). When loaded, pid is overridden to
  // uid/ObjectId, so familyMap is keyed by ObjectIds. Integer lookups then fail for any node
  // whose connections were set manually (e.g. otherAffected nodes). Build a map from stored
  // integer pids to their corresponding ObjectId pids so we can resolve stale references.
  // const originalInput = [user, ...members];
  // const storedPidToNewPid = new Map();
  // allNodes.forEach((node, i) => {
  //   const originalPid = originalInput[i]?.pid;
  //   if (
  //     originalPid !== undefined &&
  //     originalPid !== null &&
  //     String(originalPid) !== String(node.pid)
  //   ) {
  //     storedPidToNewPid.set(String(originalPid), node.pid);
  //   }
  // });

  // if (storedPidToNewPid.size > 0) {
  //   const resolveId = (id) => {
  //     if (id === null || id === undefined) return null;
  //     const resolved = storedPidToNewPid.get(String(id));
  //     return resolved !== undefined ? resolved : id;
  //   };
  //   familyMap.forEach((m) => {
  //     m.fatherId = resolveId(m.fatherId);
  //     m.motherId = resolveId(m.motherId);
  //     m.spouseIds = m.spouseIds.map(resolveId).filter((id) => id !== null);
  //   });
  // }

  familyMap.forEach((m) => {
    if (m.fatherId && m.motherId) {
      const f = familyMap.get(String(m.fatherId));
      const mo = familyMap.get(String(m.motherId));
      if (f && mo) {
        if (!f.spouseIds.includes(mo.pid)) f.spouseIds.push(mo.pid);
        if (!mo.spouseIds.includes(f.pid)) mo.spouseIds.push(f.pid);
      }
    }
    if (m.fatherId && familyMap.has(String(m.fatherId))) {
      familyMap.get(String(m.fatherId)).childrenIds.push(m.pid);
    }
    if (m.motherId && familyMap.has(String(m.motherId))) {
      familyMap.get(String(m.motherId)).childrenIds.push(m.pid);
    }
  });

  let ordered = Array.from(familyMap.values());

  dummyBySharedParentId.forEach(({ dummy, sharedParentPid }) => {
    const sharedParent = familyMap.get(sharedParentPid);
    if (!sharedParent) return;

    const realSpouseId = sharedParent.spouseIds.find((id) => {
      const node = familyMap.get(String(id));
      return node && !node.isDummy && String(id) !== String(dummy.pid);
    });

    const dummyPidStr = String(dummy.pid);
    const halfSiblings = ordered.filter((n) => {
      return (
        (String(n.fatherId) === dummyPidStr &&
          String(n.motherId) === sharedParentPid) ||
        (String(n.motherId) === dummyPidStr &&
          String(n.fatherId) === sharedParentPid)
      );
    });

    const sharedIdx = ordered.findIndex(
      (n) => String(n.pid) === sharedParentPid
    );
    const realSpouseIdx = realSpouseId
      ? ordered.findIndex((n) => String(n.pid) === String(realSpouseId))
      : -1;
    const putDummyOnLeft = realSpouseIdx === -1 || realSpouseIdx > sharedIdx;

    ordered = ordered.filter((n) => String(n.pid) !== dummyPidStr);
    let newSharedIdx = ordered.findIndex(
      (n) => String(n.pid) === sharedParentPid
    );
    if (putDummyOnLeft) {
      ordered.splice(newSharedIdx, 0, dummy);
    } else {
      ordered.splice(newSharedIdx + 1, 0, dummy);
    }

    if (halfSiblings.length > 0) {
      const halfSibIds = new Set(halfSiblings.map((n) => String(n.pid)));
      ordered = ordered.filter((n) => !halfSibIds.has(String(n.pid)));

      const probandIdx = ordered.findIndex(
        (n) => String(n.pid) === String(proband.pid)
      );
      if (probandIdx !== -1) {
        if (putDummyOnLeft) {
          ordered.splice(probandIdx, 0, ...halfSiblings);
        } else {
          ordered.splice(probandIdx + 1, 0, ...halfSiblings);
        }
      } else {
        ordered.push(...halfSiblings);
      }
    }
  });

  const idToPid = new Map();
  allNodes.forEach((m) => {
    if (m._id) idToPid.set(String(m._id), String(m.pid));
    idToPid.set(String(m.pid), String(m.pid));
  });
  const resolveTwinPid = (id) => {
    const s = String(id);
    return idToPid.get(s) || s;
  };

  const twinTypeToEngineType = (t) => {
    if (t === 'identical') return TWIN_TYPE.monozygotic;
    if (t === 'non-identical') return TWIN_TYPE.dizygotic;
    return TWIN_TYPE.unknown;
  };

  // TGCLOUD-1692: was a third hand-rolled copy of the size mapping, with the same
  // `|| quadruplet` fallback as the other two — so an imported set of five arrived as
  // a quadruplet. One definition now, in twinLinkage.
  const siblingTypeForSize = siblingTypeForCount;

  // Union-find — groups any transitively connected twin set
  const ufParent = new Map();
  const ufFind = (x) => {
    if (!ufParent.has(x)) ufParent.set(x, x);
    if (ufParent.get(x) !== x) ufParent.set(x, ufFind(ufParent.get(x)));
    return ufParent.get(x);
  };
  const ufUnion = (a, b) => ufParent.set(ufFind(a), ufFind(b));

  // Also track the twinType each member declared (used when wiring relations)
  const declaredTwinType = new Map();

  ordered.forEach((m) => {
    if (!m.twinMember) return;
    const twinPid = resolveTwinPid(m.twinMember);
    if (!familyMap.has(twinPid)) return;
    ufUnion(String(m.pid), twinPid);
    declaredTwinType.set(String(m.pid), m.twinType);
  });

  // Collect every node that ended up in a union group (includes proband-as-target)
  const components = new Map();
  ordered.forEach((m) => {
    const myPid = String(m.pid);
    if (!ufParent.has(myPid)) return;
    const root = ufFind(myPid);
    if (!components.has(root)) components.set(root, []);
    components.get(root).push(m);
  });

  components.forEach((groupMembers, root) => {
    if (groupMembers.length < 2) return;
    const sType = siblingTypeForSize(groupMembers.length);

    // Set subgroup on all members first
    groupMembers.forEach((m) => {
      m.siblingType = sType;
      m.twinSubGroupId = root;
      if (!m.twinRelations) m.twinRelations = [];
    });

    // Wire the COMPLETE graph within the set, not only the declared edges (TGCLOUD-1694).
    //
    // `twinMember` holds a single reference, so a set of three arrives as a chain: A→B and
    // B→A and C→A. Wiring only what was declared left A–C unrelated, and because path.js
    // draws a bar only for a mutual, type-matched pair, a triplet rendered with one of its
    // three bars missing. The members of one twinSubGroupId are by definition all
    // co-multiples of one another, so the missing edge is derivable, not unknowable.
    //
    // ZYGOSITY FOR AN UNDECLARED EDGE. Where every declared edge in the set agrees, that
    // type propagates — monozygosity is transitive, and a set whose declared edges are all
    // dizygotic is a set of non-identical co-multiples throughout. Where they disagree we
    // do NOT guess: the pair is wired as 'unknown', which draws the co-multiple line
    // without asserting a zygosity nobody recorded.
    const declaredTypes = new Set();
    groupMembers.forEach((m) => {
      if (!m.twinMember) return;
      const twinPid = resolveTwinPid(m.twinMember);
      if (!familyMap.has(twinPid)) return;
      declaredTypes.add(twinTypeToEngineType(m.twinType));
    });
    const inferredType =
      declaredTypes.size === 1 ? [...declaredTypes][0] : 'unknown';

    const typeForPair = new Map();
    const pairKey = (a, b) => [String(a), String(b)].sort().join('~');

    groupMembers.forEach((m) => {
      if (!m.twinMember) return;
      const twinPid = resolveTwinPid(m.twinMember);
      const twin = familyMap.get(twinPid);
      if (!twin) return;
      // A declared edge keeps the zygosity that was actually recorded for it, FIRST
      // declaration winning. Last-writer-wins let the second co-twin's blank twinType erase
      // the 'identical' the first had recorded, turning a drawn identical bar into an
      // unknown one — and `develop` was first-writer-wins here, so this keeps the existing
      // behaviour for every pair rather than quietly changing it.
      const key = pairKey(m.pid, twin.pid);
      if (!typeForPair.has(key))
        typeForPair.set(key, twinTypeToEngineType(m.twinType));
    });

    groupMembers.forEach((a) => {
      groupMembers.forEach((b) => {
        if (String(a.pid) === String(b.pid)) return;
        const type = typeForPair.get(pairKey(a.pid, b.pid)) ?? inferredType;
        if (!a.twinRelations) a.twinRelations = [];
        // Mutual on both sides, or path.js isConfirmedPair rejects it and nothing draws.
        if (!a.twinRelations.some((r) => String(r.withPid) === String(b.pid)))
          a.twinRelations.push({ withPid: b.pid, type });
      });
    });
  });

  return ordered;
};

// NORMALIZE IDs TO SEQUENTIAL NUMBERS
// Dataset with sequential numeric IDs starting from 1

export function normalizeIds(data) {
  if (!data || data.length === 0) return data;

  const idMap = new Map();
  data.forEach((person, index) => {
    idMap.set(person.pid, index + 1);
  });

  const convertId = (oldId) => {
    if (oldId === null || oldId === undefined) return null;
    return idMap.get(oldId) || oldId;
  };

  return data.map((person) => ({
    ...person,
    pid: convertId(person.pid),
    motherId: convertId(person.motherId),
    fatherId: convertId(person.fatherId),
    spouseIds: (person.spouseIds || []).map(convertId),
    childrenIds: (person.childrenIds || []).map(convertId),
    siblingIds: (person.siblingIds || []).map(convertId)
  }));
}

export const getAcronym = (data) => {
  const gender = data.genderIdentity || data.gender;

  const acronymMap = {
    'Male - AFAB': 'AFAB',
    'Male - UAAB': 'UAAB',
    'Female - AMAB': 'AMAB',
    'Female - UAAB': 'UAAB',
    'Non-binary/gender diverse - AFAB': 'AFAB',
    'Non-binary/gender diverse - AMAB': 'AMAB',
    'Non-binary/gender diverse - UAAB': 'UAAB'
  };

  return acronymMap[gender];
};

// The UPN / person number as it is DRAWN: the stored upn when there is one,
// otherwise the last "-"-separated segment of the family member id. Blank for a
// de-identified node and for reproductive nodes, which are pedigree elements
// rather than patients (they also carry no id once the data layer stops
// persisting them as members; until then this suppresses the display).
//
// Lives here — not in the renderer — because reservation and auto-spacing need
// the same string: gating the reserved row on `upn` alone left a node that
// renders the familyMemberId fallback with upn_y === null, painting it at y=0
// on the symbol, and measuring a placeholder made the row's width fictional
// (TGCLOUD-1610).
export const getDisplayUpn = (person) => {
  if (!person || person.isHide) return '';
  if (config.ENABLE_REPRO_NODES && isReproNode(person)) return '';
  if (person.upn) return person.upn;
  const memberId = person.familyMemberId || person.proBandId;
  if (!memberId) return '';
  const suffix = String(memberId).split('-').pop();
  return suffix || '';
};

// Mirror the legend's two-array data source: NCBIGene:* / HP:* entries can
// live in the dedicated phenotypes/genotypes array, or sit in `diagnoses`
// when the questionnaire's submission-time split never ran for that
// member. Both the renderer and the label-position calculator need to see
// the same merged list, otherwise the renderer paints rows but the layout
// allocates zero space — labels land at y=0, on top of the node.
const isGeneId = (id) => typeof id === 'string' && id.startsWith('NCBIGene:');
const isPhenotypeId = (id) =>
  typeof id === 'string' && id.startsWith('HP:') && id !== 'HP:999999';

// The `labelsSet` argument carries the per-type value sort (TGCLOUD-1663) and is
// OPTIONAL on all three collect helpers: with it absent they return entry order,
// which is what they always did. Every caller that renders or measures a row
// passes it, because renderer, reservation and auto-spacing must see one
// sequence — sorting in only one of them is the drift that lands labels at y=0.
//
// Note the synthetic entries appended below (a diagnosis whose id is an
// NCBIGene:/HP: code, surfaced as a genotype/phenotype row) carry NO date
// fields at all, so a date sort has to tolerate undated records even in a
// hand-entered family, not just a Sapio one.
// The single pipeline every record-bearing label goes through:
//
//   VISIBLE  → the per-record `displayOnPedigree` flag and the legend's
//              per-entity map (TGCLOUD-1792 folded these IN here; they used to
//              be repeated inline in the renderer, the reservation AND
//              auto-spacing, with a comment in each begging the other two to
//              stay in step)
//   FILTER   → the preset's per-unit filter (recency, confirmed-only)
//   SORT     → the preset's per-unit value sort (TGCLOUD-1663)
//   LIMIT    → the preset's row cap, applied LAST so "date desc, at most 1"
//              means "the most recent"
//
// Folding visibility in is what makes a limit honest: applied before it, a cap
// of 3 counted records the consumers then threw away and drew fewer than three
// rows.
//
// `hiddenCount` rides along so the caller can draw the "+ N more" row. Labels
// are captured in the PNG/PDF export with no exclusion mechanism, so a filtered
// chart is a clinical document that shows less than the record holds — the
// reader has to be told.
//
// `displayOnPedigree !== false` is the TOLERANT comparison every label pass has
// always used (a record with the field absent labels). The pie shading uses a
// strict `=== true`; the two deliberately disagree and are not unified here.
const selectRecords = (unit, rows, labelsSet, mapKey) => {
  const map = mapKey ? labelsSet?.[mapKey] : null;
  const visible = rows.filter(
    (r) => r?.displayOnPedigree !== false && (!map || map[r?.id])
  );
  return filterLabelValues(
    unit,
    sortLabelValues(unit, visible, labelsSet),
    labelsSet
  );
};

// The union a genotype row can come from: the dedicated `genotypes` array, plus
// any `diagnoses` entry whose id is an NCBIGene: code.
const genotypeUnion = (d) => {
  const out = [];
  const seen = new Set();
  for (const g of d?.genotypes || []) {
    if (g?.id && !seen.has(g.id)) {
      seen.add(g.id);
      out.push(g);
    }
  }
  for (const di of d?.diagnoses || []) {
    if (isGeneId(di?.id) && !seen.has(di.id)) {
      seen.add(di.id);
      out.push({
        id: di.id,
        name: di.name,
        displayOnPedigree: di.displayOnPedigree
      });
    }
  }
  return out;
};

const phenotypeUnion = (d) => {
  const out = [];
  const seen = new Set();
  for (const p of d?.phenotypes || []) {
    if (p?.id && !seen.has(p.id)) {
      seen.add(p.id);
      out.push(p);
    }
  }
  for (const di of d?.diagnoses || []) {
    if (isPhenotypeId(di?.id) && !seen.has(di.id)) {
      seen.add(di.id);
      out.push({
        id: di.id,
        name: di.name,
        displayOnPedigree: di.displayOnPedigree
      });
    }
  }
  return out;
};

// `{ items, hiddenCount }` for each unit. The `…Source` helpers below return
// just the rows, so every existing caller is unchanged; a caller that also draws
// the overflow row reads the pair. Both come from ONE call, so the rows and the
// count can never disagree about what was withheld.
export const selectGenotypeRows = (d, labelsSet) =>
  selectRecords('genotypes', genotypeUnion(d), labelsSet, 'genotypesMap');

export const selectPhenotypeRows = (d, labelsSet) =>
  selectRecords('phenotypes', phenotypeUnion(d), labelsSet, 'phenotypesMap');

export const selectDiagnosesRows = (d, labelsSet) =>
  selectRecords(
    'diseaseWithAge',
    Array.isArray(d?.diagnoses) ? d.diagnoses : [],
    labelsSet,
    'diseasesMap'
  );

/**
 * External patient IDs, through the same VISIBLE → SORT → FILTER → LIMIT
 * pipeline as every other record-bearing label.
 *
 * TGCLOUD-1792 offered a `limit` for the `externalPatientId` unit and the modal
 * stored it and badged it as active, but nothing applied it: both the renderer
 * and the row reservation read `d.externalPatientIds` directly. An id is a bare
 * string with no `displayOnPedigree` and no date of any kind — which is exactly
 * why the unit offers `limit` alone — so `labelsSet`'s per-unit sort and the cap
 * are all there is to apply here.
 */
export const selectExternalPatientIdRows = (d, labelsSet) => {
  const ids = Array.isArray(d?.externalPatientIds)
    ? d.externalPatientIds.filter((id) => String(id ?? '').trim() !== '')
    : [];
  return filterLabelValues(
    'externalPatientId',
    sortLabelValues('externalPatientId', ids, labelsSet),
    labelsSet
  );
};

/**
 * Whether a record-bearing label has anything to draw — kept rows, OR a
 * non-zero withheld count.
 *
 * The second half is the whole point (TGCLOUD-1792 follow-up). The phenotype and
 * genotype render passes gated on the FILTERED row count alone, so a preset
 * whose confirmed-only or recency filter hid every one of a person's phenotypes
 * drew nothing at all — no rows AND no "+ 2 more" line — and the chart, and
 * therefore the PNG and PDF exports, read as if that person had no phenotypes.
 * A clinician must never be shown an empty node for a person who has records
 * that a filter chose to hide, so the overflow row alone is reason enough to
 * render. The diagnoses pass never had the bug because its gate reads the raw
 * `d.diagnoses` array; this is the same rule, expressed once so the two passes
 * cannot drift apart again.
 */
export const hasRowsToDraw = (selection) =>
  !!selection &&
  ((selection.items?.length ?? 0) > 0 || (selection.hiddenCount ?? 0) > 0);

export const collectGenotypeSource = (d, labelsSet) =>
  selectGenotypeRows(d, labelsSet).items;

export const collectPhenotypeSource = (d, labelsSet) =>
  selectPhenotypeRows(d, labelsSet).items;

export const collectDiagnosesSource = (d, labelsSet) =>
  selectDiagnosesRows(d, labelsSet).items;

// Shared core: accumulate every label's row position below the node centre and
// the bottom extent of the whole block. getLabelPos returns the per-label y map;
// getNodeBlockHeight returns the bottom extent. Both run the SAME accumulation
// so auto-spacing reserves exactly what the renderer draws. `maxChars` is the
// wrap budget; callers pass labelMaxChars(options) for the live gap, or
// labelMaxCharsAt(candidateGap, …) when probing a hypothetical gap.
// `symbolSize` lets auto-spacing's modal preview probe an unsaved symbol size;
// defaults to the live applied config so existing callers are unchanged.
const computeLabelLayout = (
  d,
  options,
  maxChars,
  symbolSize = config.SYMBOL_SIZE
) => {
  const sym = Number(symbolSize) || 45;
  const fs = Number(options?.font_size) || 16;
  const spacing = fs * LABEL_ROW_PITCH;

  // PER-NODE label set (TGCLOUD-1465). Proband-only mode suppresses the identifying labels on everyone
  // but the proband, and it MUST be applied here as well as in the renderer: every `setY` below reserves
  // a row for a visible label, so clamping only the renderer would leave each de-identified relative
  // with a block of blank rows — and auto-spacing derives LEVEL_HEIGHT from these blocks, so the whole
  // chart would inflate. Renderer and reservation read the same helper for exactly that reason.
  //
  // `options.probandPid` is supplied by the two callers that have the member list (Canvas and
  // computeAutoSpacing); absent, labelsForNodeAt returns the set unchanged.
  const ls = labelsForNodeAt(
    options?.labelsSet || {},
    d,
    options?.probandPid ?? null
  );
  let currentY = 1.5 * sym;

  // A fertility / infertility mark — drawn below a spouseless node, or hung from
  // a couple's relationship line — can reach the first label (a gender-identity
  // acronym, a name) and clip it, especially at larger font sizes. Push the
  // first label clear of the deepest applicable mark + the text ascent.
  currentY = Math.max(currentY, fertilityLabelClearance(d, sym, fs));

  // Every row this function can reserve, as { yKey, on, lines } grouped under
  // the ORDERABLE UNIT it belongs to (TGCLOUD-1663). The conditions are
  // evaluated exactly as they always were — what changed is that the SEQUENCE
  // is no longer this function's source order but the preset's label order,
  // walked once at the bottom. Keeping the walk trivial (assign y, advance) is
  // what keeps reservation, rendering and auto-spacing in lockstep: they all
  // read the resulting y map, so none of them needs to know the order at all.
  const rows = {};
  const plan = (unit, entries) => {
    rows[unit] = entries;
  };

  const exists = (val) => {
    if (Array.isArray(val)) return val.length > 0;
    if (val === null || val === undefined) return false;
    return String(val).trim().length > 0;
  };

  const row = (yKey, on, lines = 1) => ({ yKey, on: !!on, lines });

  // Acronym
  plan('acronym', [row('acronym_y', !!getAcronym(d))]);

  // Name — suppressed for an anonymised donor/surrogate (a role label is shown
  // instead, below). Gate is flag + satellite, so non-repro naming is unchanged.
  const isReproSat = config.ENABLE_REPRO_NODES && isReproSatelliteNode(d);
  plan('name', [
    row(
      'name_y',
      !isReproSat &&
        ((ls.firstName && d.firstName) || (ls.lastName && d.lastName))
    ),
    // Anonymised donor/surrogate role label ("Sperm donor" / "Egg donor" / "Surrogate").
    // Mutually exclusive with the name row, so sharing the unit is safe.
    row('reproRole_y', isReproSat)
  ]);

  // UPN — gate on the string the renderer actually draws, not on the raw
  // fields: `familyMemberId` alone produces a visible number here.
  plan('upn', [row('upn_y', !!(ls.upn && getDisplayUpn(d)))]);

  // DoB
  plan('dob', [row('dob_y', ls.dob && exists(d.dob))]);

  // Age
  plan('age', [row('age_y', ls.age && exists(d.dob))]);

  // DoD
  plan('dod', [row('dod_y', ls.dod && exists(d.dod))]);

  // Hospital Number
  plan('hospitalNumber', [
    row('hospitalNumber_y', ls.hospitalNumber && exists(d.hospitalNumber))
  ]);

  // External Patient IDs — one line per id in the array, plus the "+ N more"
  // overflow row when the preset's limit withheld some. The count comes from the
  // shared select helper the renderer draws from, so a capped list reserves
  // exactly the rows it draws and the overflow row lands in reserved space
  // rather than on the label below it (TGCLOUD-1792).
  const externalIdSel = ls.externalPatientId
    ? selectExternalPatientIdRows(d, ls)
    : { items: [], hiddenCount: 0 };
  const externalIdLines =
    externalIdSel.items.length + (externalIdSel.hiddenCount > 0 ? 1 : 0);
  plan('externalPatientId', [
    row('externalPatientId_y', externalIdLines > 0, externalIdLines || 1)
  ]);

  // Reproductive lines beneath a pregnancy symbol (Bennett Fig 4). Flag-gated so a
  // non-repro node reserves NO rows → currentY / blockBottom byte-identical when OFF.
  // Source order here IS the stacking order: loss/SB label → gestational age →
  // sex-determination note → affected karyotype/finding → fetus name.
  //
  // Each row is ALSO gated on its Reproductive label toggle, and the reserve must mirror the
  // Canvas render filter exactly — a row reserved for a label that no longer prints leaves a
  // blank gap, which is why both sides go through `reproLabelOn`.
  const preg =
    config.ENABLE_REPRO_NODES && isPregnancyNode(d)
      ? d.pregnancy || d.data?.pregnancy
      : null;
  // Reserve the loss-label row only when a label actually prints (ectopic → "ECT").
  // SAB is a bare triangle and TOP is marked by the strike-through, so neither
  // reserves a row — otherwise the gestational-age line would sit below a blank gap.
  //
  // Source order WITHIN this unit is the stacking order (Bennett Fig 4): loss/SB
  // label → gestational age → sex-determination note → karyotype → fetus name.
  // The unit is locked precisely so this clinical sequence is not reorderable.
  plan('reproductive', [
    row(
      'reproLoss_y',
      reproLabelOn(ls, 'pregnancyOutcome') &&
        !!preg &&
        !!pregnancyLossRenderLabel(d)
    ),
    row(
      'stillbirth_y',
      reproLabelOn(ls, 'pregnancyOutcome') && !!preg && isStillbirth(d)
    ),
    row(
      'gestation_y',
      reproLabelOn(ls, 'gestationalAge') &&
        !!preg &&
        exists(preg.gestationalWeeks)
    ),
    // Sex-determination note is drawn ONLY for an ongoing sex-known pregnancy
    // (matches the renderer + Bennett §2.1); gate the reserve identically so a
    // non-ongoing pregnancy that happens to carry the field reserves no phantom row.
    row(
      'sexDetermination_y',
      reproLabelOn(ls, 'fetalSexMethod') &&
        !!preg &&
        isOngoingPregnancy(d) &&
        preg.fetalSexKnown &&
        exists(preg.sexDeterminationMethod)
    ),
    row(
      'karyotype_y',
      reproLabelOn(ls, 'fetusKaryotype') && !!preg && exists(preg.karyotype)
    ),
    row(
      'fetusName_y',
      reproLabelOn(ls, 'fetusName') &&
        !!preg &&
        exists(d.fetusName ?? d.data?.fetusName)
    ),
    // TGCLOUD-1597 — the donor role beside an IDENTIFIED donor. Deliberately NOT gated on `preg`
    // like its neighbours: a promoted donor is a real person, not a pregnancy node, so a `preg`
    // gate would reserve nothing and the label would land on top of the row below it.
    row('donorRole_y', reproLabelOn(ls, 'donorRole') && !!donorRoleLabelOf(d))
  ]);

  // Stacked header + item-list labels (Consultation Date / Diagnosis / Tests /
  // Test Comment). Reserve the EXACT number of rows each will render — a header
  // plus one per record since TGCLOUD-1781 — using the same pure helper the
  // Canvas renderer uses, so a multi-row block can never overlap the labels
  // stacked below it. No wrap budget is threaded any more: a record occupies one
  // row whatever the gap. `maxChars` is still a parameter of this function for
  // the Person Note, which does still wrap.
  for (const blk of STACKED_LABEL_BLOCKS) {
    // Mirror the Canvas render filter (which blanks the stacked clinical/test
    // blocks on de-identified nodes): reserve 0 rows for an isHide node so we
    // never leave a vertical band the renderer doesn't draw.
    const lineCount = d?.isHide ? 0 : blockLines(blk, d, ls).length;
    // Each stacked block is its own orderable unit, keyed by its toggle.
    plan(blk.key, [row(blk.yKey, lineCount > 0, lineCount || 1)]);
  }

  // Diagnoses / Phenotypes / Genotypes.
  //
  // The row count must match what the renderer will actually draw, so all three
  // read the shared select helpers — which now apply the visibility flag and the
  // legend map as well as the sort and the preset filter (TGCLOUD-1792). The
  // inline `.filter(displayOnPedigree …)` that used to sit here, in the renderer
  // and in auto-spacing is gone: three copies of one rule is how they drift.
  //
  // `hiddenCount` adds ONE more row — the "+ N more" overflow line — and it has
  // to be reserved here or it would be drawn into whatever sits below.
  const diagnosisSel = ls.diseaseWithAge
    ? selectDiagnosesRows(d, ls)
    : { items: [], hiddenCount: 0 };
  const diagnosisRows = diagnosisSel.items;
  const diagnosisLines =
    diagnosisRows.length + (diagnosisSel.hiddenCount > 0 ? 1 : 0);
  plan('diseaseWithAge', [
    row('diagnoses_y', diagnosisLines > 0, diagnosisLines || 1)
  ]);

  const phenotypeSel = selectPhenotypeRows(d, ls);
  const phenotypeRows = phenotypeSel.items;
  const phenotypeLines =
    phenotypeRows.length + (phenotypeSel.hiddenCount > 0 ? 1 : 0);
  plan('phenotypes', [
    row(
      'phenotypes_y',
      phenotypeLines > 0 && (ls.phenotypesName || ls.phenotypesID),
      phenotypeLines || 1
    )
  ]);

  // Gated on the toggles FIRST, exactly as the diagnoses and external-ID paths
  // above are, and as the Canvas genotype pass is: its render filter is
  // `hasRowsToDraw(...) && (genotypesName || genotypesID ||
  // genotypeCarrierStatus)`. Reserving off the selection alone parted company
  // with that in one case — a preset that carries a genotype FILTER while all
  // three genotype toggles are off. Nothing is drawn (every toggle is off) but
  // hiddenCount is non-zero, so the gate below would keep a blank row and push
  // every label under it down for a label the chart never prints. Resolving the
  // selection only when a toggle is on also skips the union + sort + filter work
  // for the common "genotypes off" node.
  const genotypesOn = !!(
    ls.genotypesName ||
    ls.genotypesID ||
    ls.genotypeCarrierStatus
  );
  const genotypeSel = genotypesOn
    ? selectGenotypeRows(d, ls)
    : { items: [], hiddenCount: 0 };
  const genotypeRows = genotypeSel.items;
  // Reserve when ANY row would actually draw something. The gate used to read
  // the symbol and ID toggles only, so a carrier-status-only configuration —
  // which the renderer happily draws — reserved nothing: genotypes_y was null,
  // the renderer's `genotypes_y + i * lineHeight` became NaN/0 and the rows
  // landed on top of the symbol (TGCLOUD-1610). Row COUNT stays
  // genotypeRows.length: the renderer steps by array index and skips run-less
  // rows, leaving their slot blank.
  const genotypesDraw = genotypeRows.some(
    (g) => buildGenotypeRuns(g, ls).length > 0
  );
  const genotypeLines =
    genotypeRows.length + (genotypeSel.hiddenCount > 0 ? 1 : 0);
  // ... and reserve when the filter hid EVERY genotype, too. `genotypesDraw` is
  // false in that case (there is no kept row to build runs from), so the gate
  // used to leave genotypes_y null while the renderer still had a "+ N more"
  // line to draw — `null + i * lineHeight` puts it at 0, on top of the symbol.
  // The phenotype gate above already reads a line count and so needed no
  // equivalent change.
  plan('genotypes', [
    row(
      'genotypes_y',
      genotypesDraw || genotypeSel.hiddenCount > 0,
      genotypeLines || 1
    )
  ]);

  // Person Note — reserve every wrapped row. The renderer wraps the note into N
  // lines while this reserved exactly one, so a multi-line note under-reported
  // the block height that auto-spacing derives LEVEL_HEIGHT from. It defaults to
  // last in DEFAULT_LABEL_ORDER; it is no longer forced there.
  const noteLines =
    ls.personNote && !d?.isHide && exists(d.personNote)
      ? estimateNoteLines(d.personNote, maxChars)
      : 0;
  plan('personNote', [row('com_y', noteLines > 0, noteLines || 1)]);

  // Lay the planned rows out in the configured unit order. Every yKey is
  // present in the result — null when its row does not draw — because callers
  // read the map by key and a missing key would read as an unpositioned label.
  const yPositions = {};
  Object.values(rows).forEach((entries) =>
    entries.forEach((r) => {
      yPositions[r.yKey] = null;
    })
  );

  effectiveLabelOrder(ls).forEach((unit) => {
    (rows[unit] || []).forEach((r) => {
      if (!r.on) return;
      yPositions[r.yKey] = currentY;
      currentY += spacing * r.lines;
    });
  });

  return { yPositions, blockBottom: currentY };
};

// Per-label y positions below the node centre (renderer + reservation use this).
export const getLabelPos = (d, options) =>
  computeLabelLayout(d, options, labelMaxChars(options)).yPositions;

// Bottom extent (px below the node centre) of the whole label stack. Used by
// auto-spacing to derive LEVEL_HEIGHT. `maxChars` defaults to the live gap; pass
// labelMaxCharsAt(candidateGap, …) to probe a hypothetical gap.
export const getNodeBlockHeight = (
  d,
  options,
  maxChars = labelMaxChars(options),
  symbolSize = config.SYMBOL_SIZE
) => computeLabelLayout(d, options, maxChars, symbolSize).blockBottom;

export const getName = ({ data, options }) => {
  const firstName = data?.firstName || '';
  const lastName = data?.lastName || '';

  const showFirstName = options.labelsSet.firstName;
  const showLastName = options.labelsSet.lastName;

  if (showFirstName && showLastName) return firstName + ' ' + lastName;
  else if (showFirstName) return firstName;
  else if (showLastName) return lastName;
  else return '';
};

// `size` is the symbol size the bracket is drawn against. It defaults to the chart's
// own SYMBOL_SIZE, so every existing call is byte-identical; nodeMarkPaths passes a
// small value to draw the same bracket as an 18px menu icon (TGCLOUD-1644).
export const get_bracket = (dx, dy, indent, size = config.SYMBOL_SIZE) => {
  const h = size * 1.58;
  return (
    'M' +
    (dx + indent) +
    ',' +
    dy +
    'L' +
    dx +
    ' ' +
    dy +
    'L' +
    dx +
    ' ' +
    (dy + h) +
    'L' +
    dx +
    ' ' +
    (dy + h) +
    'L' +
    (dx + indent) +
    ',' +
    (dy + h)
  );
};

export function wrap(text, width) {
  text.each(function () {
    const textElement = d3.select(this);
    const originalText = textElement.text();

    const lines = originalText.split(/\r?\n/);
    // Line spacing must track the RENDERED font size. Canvas sets `font-size` on
    // this element from the user's Pedigree Settings before calling wrap(); a
    // fixed line height (the old 45 * 1.3 ≈ 58px) left wrapped note lines
    // overlapping once the font was enlarged. 1.35 matches the inter-label
    // spacing in getLabelPos so the note's lines share the same vertical rhythm.
    const fontSize = parseFloat(textElement.attr('font-size')) || 45;
    const lineHeight = fontSize * 1.35;

    textElement.text('');

    let global_lineNumber = 0;
    lines.forEach((lineText, i) => {
      const words = lineText.split(/\s+/).reverse();

      let word;
      let line = [];
      let lineNumber = global_lineNumber + i;
      let y = textElement.attr('y');

      let tspan = textElement
        .append('tspan')
        .attr('x', 0)
        .attr('y', y)
        .attr('dy', lineNumber * lineHeight + 'px');

      while ((word = words.pop())) {
        line.push(word);
        tspan.text(line.join(' '));

        if (tspan.node().getComputedTextLength() > width) {
          line.pop();
          tspan.text(line.join(' '));

          line = [word];
          ++global_lineNumber;
          tspan = textElement
            .append('tspan')
            .attr('x', 0)
            .attr('y', y)
            .attr('dy', ++lineNumber * lineHeight + 'px')
            .text(word);
        }
      }
    });
  });
}

export const getRelationshipForNewPerson = (node, user, relationType) => {
  if (node.pid === user.pid || !node.relationship || node.relationship === '') {
    return relationType;
  }

  const nodeRel = node.relationship;

  if (nodeRel === relationship.father) {
    if (relationType === relationship.father) return relationship.fathersFather;
    if (relationType === relationship.mother) return relationship.fathersMother;
    if (relationType === relationship.fullBrother)
      return relationship.fathersBrother;
    if (relationType === relationship.fullSister)
      return relationship.fathersSister;
    if (relationType === relationship.son) return relationship.fullBrother;
    if (relationType === relationship.daughter) return relationship.fullSister;
  }

  if (nodeRel === relationship.mother) {
    if (relationType === relationship.father) return relationship.mothersFather;
    if (relationType === relationship.mother) return relationship.mothersMother;
    if (relationType === relationship.fullBrother)
      return relationship.mothersBrother;
    if (relationType === relationship.fullSister)
      return relationship.mothersSister;
    if (relationType === relationship.son) return relationship.fullBrother;
    if (relationType === relationship.daughter) return relationship.fullSister;
  }

  if (
    nodeRel === relationship.fullBrother ||
    nodeRel === relationship.fullSister
  ) {
    if (relationType === relationship.father) return relationship.father;
    if (relationType === relationship.mother) return relationship.mother;
    if (relationType === relationship.fullBrother)
      return relationship.fullBrother;
    if (relationType === relationship.fullSister)
      return relationship.fullSister;
    if (relationType === relationship.son) return relationship.otherAffected;
    if (relationType === relationship.daughter)
      return relationship.otherAffected;
  }

  if (
    nodeRel === relationship.fathersFather ||
    nodeRel === relationship.fathersMother
  ) {
    if (relationType === relationship.son) return relationship.fathersBrother;
    if (relationType === relationship.daughter)
      return relationship.fathersSister;
    if (relationType === relationship.father) return relationship.otherAffected;
    if (relationType === relationship.mother) return relationship.otherAffected;
    if (relationType === relationship.fullBrother)
      return relationship.otherAffected;
    if (relationType === relationship.fullSister)
      return relationship.otherAffected;
  }

  if (
    nodeRel === relationship.mothersFather ||
    nodeRel === relationship.mothersMother
  ) {
    if (relationType === relationship.son) return relationship.mothersBrother;
    if (relationType === relationship.daughter)
      return relationship.mothersSister;
    if (relationType === relationship.father) return relationship.otherAffected;
    if (relationType === relationship.mother) return relationship.otherAffected;
    if (relationType === relationship.fullBrother)
      return relationship.otherAffected;
    if (relationType === relationship.fullSister)
      return relationship.otherAffected;
  }

  if (
    nodeRel === relationship.fathersBrother ||
    nodeRel === relationship.fathersSister
  ) {
    if (relationType === relationship.father) return relationship.fathersFather;
    if (relationType === relationship.mother) return relationship.fathersMother;
    if (relationType === relationship.fullBrother)
      return relationship.fathersBrother;
    if (relationType === relationship.fullSister)
      return relationship.fathersSister;
    if (relationType === relationship.son) return relationship.otherAffected;
    if (relationType === relationship.daughter)
      return relationship.otherAffected;
  }

  if (
    nodeRel === relationship.mothersBrother ||
    nodeRel === relationship.mothersSister
  ) {
    if (relationType === relationship.father) return relationship.mothersFather;
    if (relationType === relationship.mother) return relationship.mothersMother;
    if (relationType === relationship.fullBrother)
      return relationship.mothersBrother;
    if (relationType === relationship.fullSister)
      return relationship.mothersSister;
    if (relationType === relationship.son) return relationship.otherAffected;
    if (relationType === relationship.daughter)
      return relationship.otherAffected;
  }

  return relationship.otherAffected;
};

// Identifiers for the actions offered by the "Add Relative" menu.
export const RELATIVE_ACTIONS = {
  parents: 'parents',
  grandparents: 'grandparents',
  spouse: 'spouse',
  brother: 'brother',
  sister: 'sister',
  son: 'son',
  daughter: 'daughter',
  unknown: 'unknown'
};

const ALL_RELATIVE_ACTIONS = Object.values(RELATIVE_ACTIONS);

// - Every other node keeps the full, unrestricted menu.
export const getAllowedRelativeActions = (node) => {
  const person = node?.pid ? node : node?.data;
  if (!person) return new Set(ALL_RELATIVE_ACTIONS);

  if (person.isProxy) {
    return new Set([
      RELATIVE_ACTIONS.son,
      RELATIVE_ACTIONS.daughter,
      RELATIVE_ACTIONS.unknown
    ]);
  }

  const ownsProxy =
    Array.isArray(person.proxyIds) && person.proxyIds.length > 0;
  if (ownsProxy) {
    return new Set([
      RELATIVE_ACTIONS.parents,
      RELATIVE_ACTIONS.grandparents,
      RELATIVE_ACTIONS.spouse,
      RELATIVE_ACTIONS.brother,
      RELATIVE_ACTIONS.sister,
      RELATIVE_ACTIONS.unknown
    ]);
  }

  return new Set(ALL_RELATIVE_ACTIONS);
};
